/**
 * CRM Tools Panel — embedded in /crm-app for authenticated CRM users.
 * Provides CSV export, CSV import, and AI deal summary trigger.
 */
import { useState } from "react";
import { useCRMAuth } from "@/contexts/CRMAuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { API } from "@/App";
import { Download, Upload, Sparkles, FileText } from "lucide-react";

export const CRMToolsPanel = ({ deals = [], onDataChanged }) => {
  const { crmUser } = useCRMAuth();
  const [importing, setImporting] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [selectedDealId, setSelectedDealId] = useState(deals[0]?.deal_id || "");

  if (!crmUser) return null;

  const downloadCSV = async (kind) => {
    const token = localStorage.getItem("crm_auth_token");
    try {
      const res = await fetch(`${API}/crm/tools/export/${kind}.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `crm_${kind}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${kind} exported`);
    } catch {
      toast.error(`Failed to export ${kind}`);
    }
  };

  const importCSV = async (kind, file) => {
    if (!file) return;
    setImporting(true);
    const token = localStorage.getItem("crm_auth_token");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch(`${API}/crm/tools/import/${kind}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Import failed");
      toast.success(`Imported ${data.imported} ${kind}${data.skipped ? ` (${data.skipped} skipped)` : ""}`);
      onDataChanged?.();
    } catch (err) {
      toast.error(err.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const runAISummary = async () => {
    if (!selectedDealId || String(selectedDealId).startsWith("demo-")) {
      toast.error("Select a real deal first");
      return;
    }
    setAiLoading(true);
    setAiResult(null);
    const token = localStorage.getItem("crm_auth_token");
    try {
      const res = await fetch(`${API}/crm/tools/ai/deal-summary`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ deal_id: selectedDealId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "AI failed");
      setAiResult(data);
      toast.success("AI summary generated");
      onDataChanged?.();
    } catch (err) {
      toast.error(err.message || "AI summary failed");
    } finally {
      setAiLoading(false);
    }
  };

  const realDeals = deals.filter((d) => d.deal_id && !String(d.deal_id).startsWith("demo-"));

  return (
    <section className="py-8 bg-slate-50 border-y border-slate-200" data-testid="crm-tools-panel">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-indigo-600" />
          Tools
        </h2>

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Export */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
              <Download className="h-4 w-4 text-indigo-600" /> Export
            </h3>
            <p className="text-xs text-slate-500 mb-3">Download all your data as CSV.</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => downloadCSV("contacts")} className="flex-1" data-testid="export-contacts-btn">
                Contacts
              </Button>
              <Button size="sm" variant="outline" onClick={() => downloadCSV("deals")} className="flex-1" data-testid="export-deals-btn">
                Deals
              </Button>
            </div>
          </div>

          {/* Import */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
              <Upload className="h-4 w-4 text-indigo-600" /> Bulk Import
            </h3>
            <p className="text-xs text-slate-500 mb-3">CSV must include a <code className="bg-slate-100 px-1 rounded">name</code> column.</p>
            <div className="space-y-2">
              <label className="flex items-center justify-center w-full px-3 py-2 border border-dashed border-slate-300 rounded-md text-xs text-slate-600 hover:bg-slate-50 cursor-pointer">
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => importCSV("contacts", e.target.files?.[0])}
                  disabled={importing}
                  data-testid="import-contacts-input"
                />
                {importing ? "Uploading..." : "Upload contacts CSV"}
              </label>
              <label className="flex items-center justify-center w-full px-3 py-2 border border-dashed border-slate-300 rounded-md text-xs text-slate-600 hover:bg-slate-50 cursor-pointer">
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => importCSV("deals", e.target.files?.[0])}
                  disabled={importing}
                  data-testid="import-deals-input"
                />
                {importing ? "Uploading..." : "Upload deals CSV"}
              </label>
            </div>
          </div>

          {/* AI Deal Summary */}
          <div className="bg-gradient-to-br from-indigo-600 to-violet-700 text-white rounded-xl p-5 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-[60px]" />
            <h3 className="font-semibold mb-2 flex items-center gap-2 relative">
              <Sparkles className="h-4 w-4" /> AI Deal Coach
            </h3>
            <p className="text-xs text-indigo-100 mb-3 relative">Get next-best action + refined win probability for any deal.</p>
            <div className="space-y-2 relative">
              <select
                value={selectedDealId}
                onChange={(e) => setSelectedDealId(e.target.value)}
                disabled={realDeals.length === 0}
                className="w-full text-sm rounded-md bg-white/10 border border-white/20 text-white px-3 py-2 placeholder:text-indigo-200"
                data-testid="ai-deal-select"
              >
                <option value="" className="text-slate-900">
                  {realDeals.length === 0 ? "No deals yet — add or import some" : "Select a deal..."}
                </option>
                {realDeals.map((d) => (
                  <option key={d.deal_id} value={d.deal_id} className="text-slate-900">
                    {d.name} • £{(d.value || 0).toLocaleString()}
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                onClick={runAISummary}
                disabled={aiLoading || !selectedDealId || realDeals.length === 0}
                className="w-full bg-white text-indigo-700 hover:bg-indigo-50 font-semibold"
                data-testid="ai-summary-btn"
              >
                {aiLoading ? "Analyzing..." : "Generate AI Summary"}
              </Button>
            </div>
          </div>
        </div>

        {/* AI Result */}
        {aiResult && (
          <div className="mt-4 bg-white border border-indigo-200 rounded-xl p-5" data-testid="ai-result">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-indigo-600" /> AI Insights
              </h3>
              <div className="flex items-center gap-2">
                <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200">
                  Refined probability: {aiResult.refined_probability}%
                </Badge>
              </div>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Summary</p>
                <p className="text-slate-800">{aiResult.summary}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Recommended next action</p>
                <p className="text-indigo-700 font-medium">{aiResult.next_action}</p>
              </div>
              {aiResult.risk_factors?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Risk factors</p>
                  <ul className="list-disc list-inside text-slate-700 space-y-0.5">
                    {aiResult.risk_factors.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
